export type Result<T, E = unknown> = { ok: true; value: T } | { ok: false; error: E }

/**
 * Creates a successful Result containing a value.
 */
export function resultOk<T>(value: T): Result<T> {
  return { ok: true, value }
}

/**
 * Creates a failed Result containing an error.
 */
export function resultError<T, E>(error: E): Result<T, E> {
  return { ok: false, error }
}

/**
 * Safely extracts the value from a Result, returning a fallback value if the
 * Result contains an error.
 *
 * @example
 * const result = resultOk(42)
 * const value = valueOrFallback(result, 0) // value = 42
 *
 * const errorResult = resultError('error')
 * const fallbackValue = valueOrFallback(errorResult, 0) // fallbackValue = 0
 */
export function valueOrFallback<T, E>(result: Result<T, E>, fallback: T): T {
  if (result.ok) {
    return result.value
  }

  return fallback
}

/**
 * Extracts the value from a Result, throwing the error if the Result contains
 * an error.
 *
 * @example
 * const result = resultOk(42)
 * const value = valueOrThrow(result) // value = 42
 *
 * const errorResult = resultError('error')
 * valueOrThrow(errorResult) // throws 'error'
 */
export function valueOrThrow<T, E>(result: Result<T, E>): T {
  if (result.ok) {
    return result.value
  }

  throw result.error
}

/**
 * Extracts the error from a Result, returning undefined if has no error.
 *
 * @example
 * const result = resultOk(42)
 * const value = maybeError(result) // value = undefined
 *
 * const errorResult = resultError('error')
 * const error = maybeError(errorResult) // value = 'error'
 */
export function maybeError<T, E>(result: Result<T, E>): E | undefined {
  if (!result.ok) {
    return result.error
  }

  return undefined
}

/**
 * Extracts the value from a Result, returning undefined if no value.
 *
 * @example
 * const result = resultOk(42)
 * const value = maybeValue(result) // value = 42
 *
 * const errorResult = resultError('error')
 * const value = maybeValue(errorResult) // value = undefined
 */
export function maybeValue<T, E>(result: Result<T, E>): T | undefined {
  if (result.ok) {
    return result.value
  }

  return undefined
}
